<script setup lang="ts">
import { Button, ZhPanel, ZhSectionHeading } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { api, type ConnectionRow, formatAgo, type Me } from '@/api'
import CopyButton from '@/components/CopyButton.vue'
import DocsLink from '@/components/DocsLink.vue'

/**
 * #888: how a person connects Claude, ChatGPT or any MCP client to their own mail, where they already are: on
 * Account, beside their address. The server address to paste, the two short walkthroughs, and the apps this person
 * has connected, each with Revoke, so nobody has to be sent the address and the steps by hand. Only the person's own
 * connections are listed: every connection in the enterprise is on Settings → Connected apps.
 */
const props = defineProps<{ me: Me }>()

const connections = ref<ConnectionRow[]>([])
const loading = ref(true)
const busy = ref(false)
const error = ref<string | null>(null)
const now = ref(Date.now())

/** The connections this person approved, live ones first. Everybody else's stay out of their own profile. */
const mine = computed(() =>
    connections.value
        .filter(row => row.approvedBy?.id === props.me.user.id)
        .sort((a, b) => Number(Boolean(a.revokedAt)) - Number(Boolean(b.revokedAt))),
)

/** What to call a connection: the name its client gave at consent, or the client id when it gave none. */
function label(row: ConnectionRow): string {
    return row.clientName ?? row.clientId
}

/** What the app may do with this person's mail, in the words the consent screen used; nothing when it holds no mail. */
function mailAccess(row: ConnectionRow): string | null {
    if (row.scopes.includes('mail:write')) {
        return 'Reads, answers and files your mail'
    }
    if (row.scopes.includes('mail:read')) {
        return 'Reads your mail'
    }
    return null
}

async function load(): Promise<void> {
    try {
        connections.value = await api.connections()
        error.value = null
    } catch {
        error.value = 'Could not load your connected apps.'
    } finally {
        loading.value = false
    }
}

/** Revoke takes effect on the app's next request, so the list is reloaded rather than patched in place. */
async function revoke(row: ConnectionRow): Promise<void> {
    if (!confirm(`Disconnect "${label(row)}"? Its next request is refused.`)) {
        return
    }
    busy.value = true
    error.value = null
    try {
        await api.revokeConnection(row.id)
        await load()
    } catch {
        error.value = 'That did not work.'
    } finally {
        busy.value = false
    }
}

onMounted(load)
</script>

<template>
	<ZhPanel measure="prose" class="space-y-3" aria-labelledby="account-connect-heading" data-testid="connect-mail">
		<ZhSectionHeading id="account-connect-heading">Read your mail from Claude or ChatGPT</ZhSectionHeading>
		<div class="space-y-2 text-sm">
			<p class="text-muted-foreground">
				Give an app that speaks MCP this address, sign in with Zero Human when it asks, and tick what it may do with
				your mail. <strong>Read</strong> lets it list and open your mail; <strong>Write</strong> lets it reply, send
				and file as you. Nothing it does with your mail waits for anyone's approval.
			</p>
			<p v-if="me.mcpUrl" class="flex flex-wrap items-center gap-2" data-testid="mcp-address">
				<code class="bg-muted px-2 py-1 font-mono text-xs" data-testid="mcp-address-text">{{ me.mcpUrl }}</code>
				<CopyButton :value="me.mcpUrl" label="Copy address" />
			</p>
			<ol class="list-decimal space-y-1 pl-5">
				<li data-testid="connect-claude">
					<strong>Claude:</strong> Settings → Connectors → Add custom connector. Paste the address, add it, then
					sign in with Zero Human and choose what it may do.
				</li>
				<li data-testid="connect-chatgpt">
					<strong>ChatGPT:</strong> Settings → Apps &amp; Connectors → Create. Paste the address, create it, then
					sign in with Zero Human and choose what it may do.
				</li>
			</ol>
			<p class="text-muted-foreground">
				Any other MCP client connects the same way: the full steps are in the docs.
				<DocsLink path="/docs/mcp/connected-apps" />
			</p>
		</div>

		<h3 class="text-sm font-medium">Apps you connected</h3>
		<p v-if="error" class="text-destructive text-sm" data-testid="connect-error">{{ error }}</p>
		<p v-else-if="loading" class="text-muted-foreground text-sm">Loading…</p>
		<p v-else-if="mine.length === 0" class="text-muted-foreground text-sm" data-testid="no-own-connections">
			No app of yours is connected yet. The ones you approve appear here.
		</p>
		<ul v-else class="space-y-2 text-sm" data-testid="own-connections">
			<li
				v-for="row in mine"
				:key="row.id"
				class="flex flex-wrap items-center justify-between gap-2"
				:data-testid="`own-connection-${row.id}`"
			>
				<span class="space-x-2">
					<span :class="row.revokedAt ? 'text-muted-foreground line-through' : 'font-medium'">{{ label(row) }}</span>
					<span v-if="mailAccess(row)" class="text-muted-foreground" data-mail-access>{{ mailAccess(row) }}</span>
					<span v-else class="text-muted-foreground" data-mail-access>No access to your mail</span>
					<span class="text-muted-foreground text-xs">
						· {{ row.lastUsedAt ? `used ${formatAgo(now - Date.parse(row.lastUsedAt))}` : 'never used' }}
					</span>
				</span>
				<span v-if="row.revokedAt" class="text-muted-foreground text-xs" :data-testid="`own-revoked-${row.id}`">
					disconnected
				</span>
				<Button
					v-else
					type="button"
					size="sm"
					variant="outline"
					:data-testid="`own-revoke-${row.id}`"
					:disabled="busy"
					@click="revoke(row)"
				>
					Disconnect
				</Button>
			</li>
		</ul>
	</ZhPanel>
</template>
